import { Divider } from '@mui/material';
import {
  RiBankLine,
  RiBuilding2Line,
  RiDashboardLine,
  RiInboxArchiveLine,
  RiInboxUnarchiveLine,
  RiLeafLine,
  RiPieChartLine,
  RiSparkling2Line,
} from '@remixicon/react';
import { useRouter } from '@tanstack/react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useIsDawsonsWarehouseUser } from 'src/auth';
import { useAiV2Enabled } from 'src/hooks/useAiV2Enabled';
import { buildAppHref } from 'src/lib/routing';
import { useAiInboxOnlyMode } from 'src/modules/ai-inbox/hooks/useAiInboxOnlyMode';
import { appLinks } from 'src/routing/appLinks';
import { z } from 'zod';
import SidebarLink from './SidebarLink';
import SidebarNestedLinks from './SidebarNestedLinks';

/**
 * `useFlags()` is untyped, so entries arrive as `unknown` and are VALIDATED, not
 * merely annotated: a declared type would only hide the implicit-any diagnostic
 * while letting a malformed entry through. The BI route requires a nonempty
 * `dashboardId`, so an entry without one yields a link whose match resolves to
 * `status: 'error'`.
 */
// `.min(1)` alone still admits '   ', which is truthy but produces a dead URL.
const presentText = z.string().refine((value) => value.trim().length > 0);

const biDashboardFlagSchema = z.object({ name: presentText, presetDashboardId: presentText, slug: presentText });

export function selectValidBiDashboards(flag: unknown): z.infer<typeof biDashboardFlagSchema>[] {
  if (!Array.isArray(flag)) return [];

  return flag.flatMap((entry) => {
    const parsed = biDashboardFlagSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
}

export default function MemberNavigation() {
  const flags = useFlags();
  const aiV2 = useAiV2Enabled();
  const router = useRouter();
  const biDashboards: unknown = flags.biDashboards;
  // Dawsons warehouse users have no accounting access; mirrors the route-level gate.
  const isDawsonsWarehouseUser = useIsDawsonsWarehouseUser();
  const { locked: aiInboxOnly } = useAiInboxOnlyMode();

  const inventoryLinks = useMemo(() => {
    // permissionsCanViewInventory gates the four inventory items.
    // productionRuns gates Production Runs independently — so a user with
    // productionRuns=true and permissionsCanViewInventory=false sees only
    // Production Runs under Warehouse.
    const links: Array<{ text: string; href: string; testId: string }> = [];
    if (flags.permissionsCanViewInventory) {
      links.push({
        text: 'Inventory Report',
        href: buildAppHref(router, appLinks.inventory()),
        testId: 'nav-inventory-report',
      });
      if (flags.inventoryTransfers) {
        links.push({
          text: 'Inventory Transfer',
          href: buildAppHref(router, appLinks.inventoryTransfers()),
          testId: 'nav-inventory-transfers',
        });
      }
      links.push(
        { text: 'Pick Tickets', href: buildAppHref(router, appLinks.viewPickTickets()), testId: 'nav-pick-tickets' },
        {
          text: 'Receiving Tickets',
          href: buildAppHref(router, appLinks.viewReceivingTickets()),
          testId: 'nav-receiving-tickets',
        }
      );
    }
    if (flags.productionRuns) {
      links.push({
        text: 'Production Runs',
        href: buildAppHref(router, appLinks.productionRuns()),
        testId: 'nav-production-runs',
      });
    }
    return links;
  }, [flags, router]);

  const accountingLinks = useMemo(() => {
    if (isDawsonsWarehouseUser) return [];
    const links = [
      { text: 'Payables', href: buildAppHref(router, appLinks.payables()), testId: 'nav-payables' },
      { text: 'Invoices', href: buildAppHref(router, appLinks.invoices()), testId: 'nav-invoices' },
    ];
    if (flags.checkPrinting) {
      links.push(
        { text: 'Payments', href: buildAppHref(router, appLinks.payments()), testId: 'nav-payments' },
        { text: 'Print Checks', href: buildAppHref(router, appLinks.printChecks()), testId: 'nav-print-checks' }
      );
    }
    if (flags.accountingReports) {
      links.push(
        { text: 'Accounts', href: buildAppHref(router, appLinks.accounts()), testId: 'nav-accounts' },
        {
          text: 'Accounting Reports',
          href: buildAppHref(router, appLinks.accountingReports()),
          testId: 'nav-accounting-reports',
        },
        { text: 'Aging Reports', href: buildAppHref(router, appLinks.agingReports()), testId: 'nav-aging-reports' }
      );
    }
    return links;
  }, [flags, isDawsonsWarehouseUser, router]);

  const analyticsLinks = useMemo(() => {
    return selectValidBiDashboards(biDashboards).map((dashboard) => ({
      text: dashboard.name,
      href: buildAppHref(
        router,
        appLinks.biDashboards({ slug: dashboard.slug, dashboardId: dashboard.presetDashboardId })
      ),
      testId: `nav-${dashboard.slug}`,
    }));
  }, [biDashboards, router]);

  const intelligenceLink = (
    <SidebarLink
      icon={() => <RiSparkling2Line />}
      href={buildAppHref(router, appLinks.chatHome())}
      text="Agriful Intelligence"
      testId="nav-agriful-intelligence"
    />
  );

  // AI Inbox-only lockdown retains one product entry and drops every other
  // section. The route-level guard in AgrifulFrame enforces this independently.
  if (aiInboxOnly) {
    if (aiV2) return intelligenceLink;

    return (
      <SidebarLink
        icon={() => <RiSparkling2Line />}
        href={buildAppHref(router, appLinks.aiInbox())}
        text="AI Inbox"
        testId="nav-ai-inbox"
      />
    );
  }

  return (
    <>
      {aiV2 && intelligenceLink}

      {!aiV2 && flags.aiChat && (
        <SidebarLink
          icon={() => <RiSparkling2Line />}
          href={buildAppHref(router, appLinks.aiInbox())}
          text="AI Inbox"
          testId="nav-ai-inbox"
        />
      )}

      {flags.orderBoard && (
        <SidebarLink
          text="Order Board"
          icon={() => <RiDashboardLine />}
          href={buildAppHref(router, appLinks.orderBoard())}
          testId="nav-order-board"
        />
      )}

      {flags.permissionsCanViewSalesOrders && (
        <SidebarLink
          icon={() => <RiInboxUnarchiveLine />}
          href={buildAppHref(router, appLinks.salesOrders())}
          text="Sales Orders"
          testId="nav-sales-orders"
        />
      )}

      {flags.permissionsCanViewPurchaseOrders && (
        <SidebarLink
          icon={() => <RiInboxArchiveLine />}
          href={buildAppHref(router, appLinks.purchaseOrders())}
          text="Purchase Orders"
          testId="nav-purchase-orders"
        />
      )}

      {flags.workOrders && (
        <SidebarLink
          icon={() => <RiLeafLine />}
          href={buildAppHref(router, appLinks.growerProducts())}
          text="Grower Products"
          testId="nav-grower-products"
        />
      )}

      <Divider sx={{ my: 1, width: '100%' }} />

      {flags.accounting && !isDawsonsWarehouseUser && (
        <SidebarNestedLinks
          text="Accounting"
          route="accounting"
          icon={() => <RiBankLine />}
          testId="nav-accounting"
          links={accountingLinks}
        />
      )}

      {(flags.permissionsCanViewInventory || flags.productionRuns) && (
        <SidebarNestedLinks
          icon={() => <RiBuilding2Line />}
          route="inventory"
          text="Warehouse"
          testId="nav-inventory"
          links={inventoryLinks}
        />
      )}

      {flags.businessIntelligence && (
        <SidebarNestedLinks
          text="Analytics"
          route="business-intelligence"
          icon={() => <RiPieChartLine />}
          testId="nav-business-intelligence"
          links={analyticsLinks}
        />
      )}
    </>
  );
}
